import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format, parseISO, isAfter, addDays, startOfMonth } from 'date-fns';
import { Button, Dimmer, Dropdown, Icon, Loader, Message, Modal, Table } from 'semantic-ui-react';
import ApiAgent from './ApiAgent';
import Calendar from './Calendar';
import Page from './Page';
import { getScheduleDateTime } from './ViewHelper';

function OperationBulkRescheduleForm({ account, plannedVisits, operationDayVisit, operation, secondOperationDayVisit, secondOperation, onCommit, onClose, initialPage = 'operation_date' }) {
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(initialPage);

    const criteria = useMemo(() => operation?.scheduling_policy?.criteria || null, [operation]);
    const canSelectDoctor = !!criteria?.can_select_operation_doctor;
    const [operationDoctorCode, setOperationDoctorCode] = useState(operationDayVisit?.doctor?.code || operation?.operation_doctors?.[0]?.code || '');

    const defaultMonth = useMemo(() => {
        const today = new Date();
        const fromDate = criteria?.appointment_date_range?.from ? parseISO(criteria.appointment_date_range.from) : null;
        return fromDate && isAfter(fromDate, today) ? fromDate : today;
    }, [criteria]);

    const dateToLimit = useMemo(() => {
        return criteria?.appointment_date_range?.to ? parseISO(criteria.appointment_date_range.to) : null;
    }, [criteria]);

    const [operationMonth, setOperationMonth] = useState(defaultMonth);
    const [operationDays, setOperationDays] = useState([]);
    const [selectedOperationDate, setSelectedOperationDate] = useState(() =>
        initialPage === 'second_operation_date' && operationDayVisit?.appointment?.at?.date
            ? parseISO(operationDayVisit.appointment.at.date)
            : null
    );
    const [selectedOperationAppointment, setSelectedOperationAppointment] = useState(() =>
        initialPage === 'second_operation_date' ? (operationDayVisit?.appointment || null) : null
    );
    const [candidateStepAppointments, setCandidateStepAppointments] = useState([]);

    const [selectedAppointmentsByVisitId, setSelectedAppointmentsByVisitId] = useState(() => {
        if (initialPage === 'second_operation_date') {
            // 1眼目の全来院（術前・術後・当日）の既存予約を初期値として設定
            const init = {};
            (plannedVisits || []).forEach(v => {
                if (v.appointment && (v.operations || []).some(op => op.id === operation?.id)) {
                    init[v.id] = v.appointment;
                }
            });
            return init;
        }
        return {};
    });
    const [resourceCodesByStepCode, setResourceCodesByStepCode] = useState(() => {
        return (operation?.scheduling_policy?.steps || []).reduce((acc, step) => {
            const selected = (step.option_resources || []).filter((r) => r.selected).map((r) => r.code);
            if (selected.length > 0) acc[step.code] = selected;
            return acc;
        }, {});
    });
    const [stepPickVisit, setStepPickVisit] = useState(null);
    const [stepPickCandidates, setStepPickCandidates] = useState([]);
    const [stepPickSelectedDate, setStepPickSelectedDate] = useState(null);
    const [stepPickMonth, setStepPickMonth] = useState(new Date());
    const [confirming, setConfirming] = useState(false);

    const secondCriteria = useMemo(() => secondOperation?.scheduling_policy?.criteria || null, [secondOperation]);
    const secondCanSelectDoctor = !!secondCriteria?.can_select_operation_doctor;
    const [secondOperationDoctorCode, setSecondOperationDoctorCode] = useState(secondOperationDayVisit?.doctor?.code || operation?.operation_doctors?.[0]?.code || '');

    const [secondOperationDays, setSecondOperationDays] = useState([]);
    const [secondOperationMonth, setSecondOperationMonth] = useState(new Date());
    const [stepPickReturnPage, setStepPickReturnPage] = useState('schedules');

    const [selectedSecondOperationDate, setSelectedSecondOperationDate] = useState(null);
    const [selectedSecondOperationAppointment, setSelectedSecondOperationAppointment] = useState(null);
    const [secondCandidateStepAppointments, setSecondCandidateStepAppointments] = useState([]);
    const [secondResourceCodesByStepCode, setSecondResourceCodesByStepCode] = useState(() => {
        return (secondOperation?.scheduling_policy?.steps || []).reduce((acc, step) => {
            const selected = (step.option_resources || []).filter((r) => r.selected).map((r) => r.code);
            if (selected.length > 0) acc[step.code] = selected;
            return acc;
        }, {});
    });

    const operationVisits = useMemo(() => {
        if (!operation?.id) {
            return [];
        }
        const all = plannedVisits.filter((visit) =>
            (visit.operations || []).some(op => op.id === operation.id && op.step?.code));
        const stepOrder = (operation?.scheduling_policy?.steps || []).map((x) => x.code);
        return all.sort((a, b) => {
            const aOp = (a.operations || []).find(op => op.id === operation.id);
            const bOp = (b.operations || []).find(op => op.id === operation.id);
            return stepOrder.indexOf(aOp?.step?.code) - stepOrder.indexOf(bOp?.step?.code);
        });
    }, [plannedVisits, operation]);

    const requiredStepCodes = useMemo(() => {
        return (operation?.scheduling_policy?.steps || []).map((x) => x.code);
    }, [operation]);

    const stepByCode = useMemo(() => {
        return (operation?.scheduling_policy?.steps || []).reduce((acc, step) => {
            acc[step.code] = step;
            return acc;
        }, {});
    }, [operation]);

    const visitByStepCode = useMemo(() => {
        return operationVisits.reduce((acc, visit) => {
            const matchingOp = (visit.operations || []).find(op => op.id === operation?.id);
            if (matchingOp?.step?.code) acc[matchingOp.step.code] = visit;
            return acc;
        }, {});
    }, [operationVisits, operation?.id]);

    const secondOperationVisits = useMemo(() => {
        if (!secondOperation?.id) return [];
        const all = plannedVisits.filter((visit) =>
            (visit.operations || []).some(op => op.id === secondOperation.id && op.step?.code));
        const stepOrder = (secondOperation?.scheduling_policy?.steps || []).map((x) => x.code);
        return all.sort((a, b) => {
            const aOp = (a.operations || []).find(op => op.id === secondOperation.id);
            const bOp = (b.operations || []).find(op => op.id === secondOperation.id);
            return stepOrder.indexOf(aOp?.step?.code) - stepOrder.indexOf(bOp?.step?.code);
        });
    }, [plannedVisits, secondOperation]);

    const secondRequiredStepCodes = useMemo(() => {
        return (secondOperation?.scheduling_policy?.steps || []).map((x) => x.code);
    }, [secondOperation]);

    const secondStepByCode = useMemo(() => {
        return (secondOperation?.scheduling_policy?.steps || []).reduce((acc, step) => {
            acc[step.code] = step;
            return acc;
        }, {});
    }, [secondOperation]);

    const secondVisitByStepCode = useMemo(() => {
        return secondOperationVisits.reduce((acc, visit) => {
            const matchingOp = (visit.operations || []).find(op => op.id === secondOperation?.id);
            if (matchingOp?.step?.code) acc[matchingOp.step.code] = visit;
            return acc;
        }, {});
    }, [secondOperationVisits, secondOperation?.id]);

    const skipAutoSelectRef = useRef(false);

    const apiSend = useCallback((method, path, json) => {
        return new Promise((resolve, reject) => {
            ApiAgent(account.auth_token).send(method, path, json, resolve, reject);
        });
    }, [account.auth_token]);

    const reloadOperationCandidates = useCallback(() => {
        if (!operation?.id) {
            return;
        }

        const dateFrom = criteria?.appointment_date_range?.from
            ? (isAfter(parseISO(criteria.appointment_date_range.from), new Date()) ? criteria.appointment_date_range.from : format(new Date(), 'yyyy-MM-dd'))
            : format(new Date(), 'yyyy-MM-dd');

        const allResources = Object.entries(resourceCodesByStepCode)
            .flatMap(([stepCode, codes]) => codes.map((r) => `${stepCode}:${r}`));
        const resourcesParam = allResources.length > 0 ? allResources.join(',') : null;

        setLoading(true);
        apiSend('GET', `/operations/${operation.id}/candidates`, {
            date_from: dateFrom,
            doctor: canSelectDoctor ? operationDoctorCode : null,
            resources: resourcesParam
        })
            .then((res) => {
                setError('');
                setOperationDays(res);
                setLoading(false);
            })
            .catch((e) => {
                setError(e.message);
                setLoading(false);
            });
    }, [apiSend, operation?.id, criteria, canSelectDoctor, operationDoctorCode, resourceCodesByStepCode]);

    const reloadSecondOperationCandidates = useCallback(() => {
        if (!secondOperation?.id) return;
        const allResources = Object.entries(secondResourceCodesByStepCode)
            .flatMap(([stepCode, codes]) => codes.map((r) => `${stepCode}:${r}`));
        const resourcesParam = allResources.length > 0 ? allResources.join(',') : null;
        // 表示中の月の初日を date_from とし、過去日にならないよう当日でクランプする。
        // これによりカレンダーの月送りで候補ウィンドウがスライドする。
        const monthStart = startOfMonth(secondOperationMonth);
        const dateFrom = isAfter(monthStart, new Date()) ? monthStart : new Date();
        setLoading(true);
        apiSend('GET', `/operations/${secondOperation.id}/candidates`, {
            date_from: format(dateFrom, 'yyyy-MM-dd'),
            doctor: secondCanSelectDoctor ? secondOperationDoctorCode : null,
            first_operation_day: selectedOperationDate
                ? format(selectedOperationDate, 'yyyy-MM-dd')
                : null,
            resources: resourcesParam
        })
            .then((days) => {
                setError('');
                setSecondOperationDays(days);
                setLoading(false);
            })
            .catch((e) => { setError(e.message); setLoading(false); });
    }, [apiSend, secondOperation?.id, secondCriteria, secondCanSelectDoctor, secondOperationDoctorCode, secondResourceCodesByStepCode, selectedOperationDate, secondOperationMonth]);

    useEffect(() => {
        if (page === 'operation_date') reloadOperationCandidates();
        if (page === 'second_operation_date') reloadSecondOperationCandidates();
    }, [page, reloadOperationCandidates, reloadSecondOperationCandidates]);

    const operationCandidateDays = operationDays
        .filter((x) => !operationDoctorCode || x.doctor === operationDoctorCode)
        .map((x) => parseISO(x.date))
        .filter((d) => !dateToLimit || !isAfter(d, dateToLimit));

    const secondOperationCandidateDays = secondOperationDays.map((x) => parseISO(x.date));

    const loadStepCandidates = useCallback((visit, stepCode, targetDate, resCodesByStep) => {
        return apiSend('GET', `/planned_visits/${visit.id}/candidates`, {
            target_day: targetDate ? format(targetDate, 'yyyy-MM-dd') : null,
            doctor: null,
            resources: (resCodesByStep[stepCode] || []).length > 0 ? resCodesByStep[stepCode] : null
        });
    }, [apiSend]);

    const refreshFirstEyeSelections = useCallback(() => {
        if (!selectedOperationAppointment?.id || !selectedOperationDate) return;
        const targetStepCodes = requiredStepCodes.filter((code) => code !== '*');
        if (targetStepCodes.length === 0) return;

        const applySteps = (stepApps) => {
            const next = { [operationDayVisit.id]: selectedOperationAppointment };
            const noVacancy = [];
            targetStepCodes.forEach((stepCode) => {
                const visit = visitByStepCode[stepCode];
                if (!visit) return; // 計画来院なしのステップはスキップ
                const sa = stepApps.find((s) => s.step === stepCode);
                if (sa?.appointment) next[visit.id] = sa.appointment;
                else noVacancy.push(stepCode);
            });
            return { next, noVacancy };
        };

        if (candidateStepAppointments.length > 0) {
            const { next, noVacancy } = applySteps(candidateStepAppointments);
            setSelectedAppointmentsByVisitId((prev) => ({ ...prev, ...next }));
            setError(noVacancy.length > 0 ? '術前・術後の空き予約が見つかりません。手術当日の日付を変更して再度お試しください。' : '');
            return;
        }

        const resources = Object.entries(resourceCodesByStepCode)
            .flatMap(([sc, codes]) => codes.map((r) => `${sc}:${r}`));
        setLoading(true);
        apiSend('GET', `/operations/${operation.id}/candidate_steps`, {
            target_day: format(selectedOperationDate, 'yyyy-MM-dd'),
            doctor: canSelectDoctor ? operationDoctorCode : null,
            resources: resources.length > 0 ? resources.join(',') : null
        }).then((stepApps) => {
            const { next, noVacancy } = applySteps(stepApps);
            setSelectedAppointmentsByVisitId((prev) => ({ ...prev, ...next }));
            setError(noVacancy.length > 0 ? '術前・術後の空き予約が見つかりません。手術当日の日付を変更して再度お試しください。' : '');
            setLoading(false);
        }).catch((e) => { setError(e.message); setLoading(false); });
    }, [selectedOperationAppointment, selectedOperationDate, requiredStepCodes, visitByStepCode,
        candidateStepAppointments, apiSend, operation?.id, canSelectDoctor, operationDoctorCode,
        operationDayVisit.id, resourceCodesByStepCode]);

    const refreshSecondEyeSelections = useCallback(() => {
        if (!selectedSecondOperationAppointment?.id || !selectedSecondOperationDate || !secondOperation?.id || !secondOperationDayVisit) return;
        const stepCodes = secondRequiredStepCodes.filter((code) => code !== '*');
        if (stepCodes.length === 0) return;

        const applySteps = (stepApps) => {
            const next = { [secondOperationDayVisit.id]: selectedSecondOperationAppointment };
            const noVacancy = [];
            stepCodes.forEach((stepCode) => {
                const visit = secondVisitByStepCode[stepCode];
                if (!visit) return; // 計画来院なしのステップはスキップ
                const sa = stepApps.find((s) => s.step === stepCode);
                if (sa?.appointment) next[visit.id] = sa.appointment;
                else noVacancy.push(stepCode);
            });
            return { next, noVacancy };
        };

        if (secondCandidateStepAppointments.length > 0) {
            const { next, noVacancy } = applySteps(secondCandidateStepAppointments);
            setSelectedAppointmentsByVisitId((prev) => ({ ...prev, ...next }));
            setError(noVacancy.length > 0 ? '２眼目の術前・術後の空き予約が見つかりません。２眼目の手術当日の日付を変更して再度お試しください。' : '');
            return;
        }

        const resources = Object.entries(secondResourceCodesByStepCode)
            .flatMap(([sc, codes]) => codes.map((r) => `${sc}:${r}`));
        setLoading(true);
        apiSend('GET', `/operations/${secondOperation.id}/candidate_steps`, {
            target_day: format(selectedSecondOperationDate, 'yyyy-MM-dd'),
            resources: resources.length > 0 ? resources.join(',') : null
        }).then((stepApps) => {
            const { next, noVacancy } = applySteps(stepApps);
            setSelectedAppointmentsByVisitId((prev) => ({ ...prev, ...next }));
            setError(noVacancy.length > 0 ? '２眼目の術前・術後の空き予約が見つかりません。２眼目の手術当日の日付を変更して再度お試しください。' : '');
            setLoading(false);
        }).catch((e) => { setError(e.message); setLoading(false); });
    }, [selectedSecondOperationAppointment, selectedSecondOperationDate, secondRequiredStepCodes, secondVisitByStepCode,
        secondCandidateStepAppointments, apiSend, secondOperation?.id,
        secondOperationDayVisit?.id, secondResourceCodesByStepCode]);

    useEffect(() => {
        if (page === 'schedules' || page === 'second_schedules') {
            if (skipAutoSelectRef.current) {
                skipAutoSelectRef.current = false;
                return;
            }
            if (page === 'schedules') refreshFirstEyeSelections();
            if (page === 'second_schedules') refreshSecondEyeSelections();
        }
    }, [page, refreshFirstEyeSelections, refreshSecondEyeSelections]);
    const remainingModifyCount = (() => {
        if (!criteria?.modify_limit) {
            return null;
        }
        return criteria.modify_limit.limit_count - criteria.modify_limit.current_count;
    })();

    const isAllFirstEyeStepsSelected = requiredStepCodes.every((stepCode) => {
        const visit = visitByStepCode[stepCode];
        if (!visit) return true; // 計画来院なしのステップはスキップ
        return !!selectedAppointmentsByVisitId[visit.id]?.id;
    });

    const isAllSecondEyeStepsSelected = secondRequiredStepCodes.every((stepCode) => {
        const visit = secondVisitByStepCode[stepCode];
        if (!visit) return true; // 計画来院なしのステップはスキップ
        return !!selectedAppointmentsByVisitId[visit.id]?.id;
    });

    const commitAllAppointments = () => {
        if (!isAllFirstEyeStepsSelected || (secondOperation && !isAllSecondEyeStepsSelected)) {
            return;
        }

        setLoading(true);
        const firstCommits = initialPage === 'second_operation_date' ? [] : requiredStepCodes.flatMap((stepCode) => {
            const visit = visitByStepCode[stepCode];
            if (!visit) return []; // 計画来院なしのステップはスキップ
            const appointment = selectedAppointmentsByVisitId[visit.id];
            const resources = (resourceCodesByStepCode[stepCode] || []).length > 0
                ? resourceCodesByStepCode[stepCode]
                : null;
            return [apiSend('PUT', `/planned_visits/${visit.id}/appoint`, { appointment: appointment.id, resources })];
        });
        const secondCommits = secondOperation ? secondRequiredStepCodes.flatMap((stepCode) => {
            const visit = secondVisitByStepCode[stepCode];
            if (!visit) return []; // 計画来院なしのステップはスキップ
            const appointment = selectedAppointmentsByVisitId[visit.id];
            const resources = (secondResourceCodesByStepCode[stepCode] || []).length > 0
                ? secondResourceCodesByStepCode[stepCode]
                : null;
            return [apiSend('PUT', `/planned_visits/${visit.id}/appoint`, { appointment: appointment.id, resources })];
        }) : [];
        Promise.all([...firstCommits, ...secondCommits])
            .then(() => {
                setError('');
                setLoading(false);
                setConfirming(false);
                onCommit();
            })
            .catch((e) => {
                setError(e.message);
                setLoading(false);
                setConfirming(false);
            });
    };

    const openStepPickPage = (visit) => {
        const matchingOp = (visit.operations || []).find(op =>
            op.id === operation?.id || op.id === secondOperation?.id);
        const stepCode = matchingOp?.step?.code;
        if (!stepCode || stepCode === '*') {
            return;
        }

        const isSecondOp = secondOperation && matchingOp?.id === secondOperation.id;
        const targetStepCodes = isSecondOp ? secondRequiredStepCodes : requiredStepCodes;
        const targetVisitByStepCode = isSecondOp ? secondVisitByStepCode : visitByStepCode;
        const targetStepByCode = isSecondOp ? secondStepByCode : stepByCode;
        const targetDate = isSecondOp ? selectedSecondOperationDate : selectedOperationDate;
        const targetResCodesByStep = isSecondOp ? secondResourceCodesByStepCode : resourceCodesByStepCode;

        setLoading(true);
        loadStepCandidates(visit, stepCode, targetDate, targetResCodesByStep)
            .then((res) => {
                const stepIndex = targetStepCodes.indexOf(stepCode);
                const prevStepCode = stepIndex > 0 ? targetStepCodes[stepIndex - 1] : null;
                const nextStepCode = stepIndex < targetStepCodes.length - 1 ? targetStepCodes[stepIndex + 1] : null;
                const prevVisit = prevStepCode ? targetVisitByStepCode[prevStepCode] : null;
                const nextVisit = nextStepCode ? targetVisitByStepCode[nextStepCode] : null;
                const prevSelectedApp = prevVisit ? selectedAppointmentsByVisitId[prevVisit.id] : null;
                const nextSelectedApp = nextVisit ? selectedAppointmentsByVisitId[nextVisit.id] : null;
                const prevStep = prevStepCode ? targetStepByCode[prevStepCode] : null;
                const currentStep = targetStepByCode[stepCode];

                let filtered = res;
                if (prevSelectedApp && 0 <= prevStep?.next_step_margin) {
                    const earliestDate = format(addDays(parseISO(prevSelectedApp.at.date), prevStep.next_step_margin), 'yyyy-MM-dd');
                    filtered = filtered.filter((c) => c.at.date >= earliestDate);
                }
                if (nextSelectedApp && 0 <= currentStep?.next_step_margin) {
                    const latestDate = format(addDays(parseISO(nextSelectedApp.at.date), -currentStep.next_step_margin), 'yyyy-MM-dd');
                    filtered = filtered.filter((c) => c.at.date <= latestDate);
                }

                setError('');
                setStepPickVisit(visit);
                setStepPickCandidates(filtered);
                setStepPickSelectedDate(null);
                setStepPickMonth(filtered.length > 0 ? parseISO(filtered[0].at.date) : new Date());
                setStepPickReturnPage(isSecondOp ? 'second_schedules' : 'schedules');
                setPage('step_date');
                setLoading(false);
            })
            .catch((e) => {
                setError(e.message);
                setLoading(false);
            });
    };

    const renderStepRows = (stepCodes, sbCode, vbCode, resCodes, onResChange) => stepCodes.map((stepCode) => {
        const step = sbCode[stepCode];
        const visit = vbCode[stepCode];
        if (!visit) return null;
        const selected = selectedAppointmentsByVisitId[visit.id];
        const isOperationDay = stepCode === '*';
        const currentAt = visit.appointment?.at || null;
        const optionResources = (!isOperationDay && step) ? (step.option_resources || []) : [];
        return (
            <Table.Row key={visit.id} style={{ cursor: isOperationDay ? 'default' : 'pointer' }} onClick={() => openStepPickPage(visit)}>
                <Table.Cell>
                    <div>{step?.name || (visit.operations || [])[0]?.step?.name}</div>
                </Table.Cell>
                <Table.Cell>
                    {currentAt && (
                        <div style={{ fontSize: '0.9em', color: '#888', textDecoration: 'line-through' }}>
                            {getScheduleDateTime(currentAt)}
                            {visit.appointment?.doctor && (
                                <span style={{ marginLeft: '0.5em' }}>{visit.appointment.doctor.name}</span>
                            )}
                        </div>
                    )}
                    <div style={{ fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={!selected ? { color: '#c0392b' } : undefined}>
                            {selected ? (
                                <>
                                    {getScheduleDateTime(selected.at)}
                                    {selected.doctor && (
                                        <span style={{ marginLeft: '0.5em', color: '#444' }}>{selected.doctor.name}</span>
                                    )}
                                </>
                            ) : <><Icon name="exclamation circle" />未選択</>}
                        </span>
                        {!isOperationDay && <span style={{ fontSize: '0.85em', color: '#666' }}>他の日時を選択<Icon name="chevron right" style={{ marginLeft: '0.25em' }} /></span>}
                    </div>
                    {optionResources.length > 0 && (
                        <div style={{ marginTop: '0.5em', backgroundColor: '#f0f4ff', border: '1px solid #c8d4f0', borderRadius: '4px', padding: '0.4em 0.6em', display: 'flex', flexDirection: 'column', gap: '0.3em' }} onClick={(e) => e.stopPropagation()}>
                            {optionResources.map((resource) => (
                                <div key={resource.code} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '0.85em' }}>{resource.name}</span>
                                    <Button.Group size="mini">
                                        <Button
                                            positive={(resCodes[stepCode] || []).includes(resource.code)}
                                            onClick={() => onResChange(stepCode, [...(resCodes[stepCode] || []).filter(c => c !== resource.code), resource.code])}
                                        >あり</Button>
                                        <Button
                                            positive={!(resCodes[stepCode] || []).includes(resource.code)}
                                            onClick={() => onResChange(stepCode, (resCodes[stepCode] || []).filter(c => c !== resource.code))}
                                        >なし</Button>
                                    </Button.Group>
                                </div>
                            ))}
                        </div>
                    )}
                    {visit.instruction &&
                        <div style={{ marginTop: '0.4em', color: '#c0392b', whiteSpace: 'pre-wrap' }}>
                            {visit.instruction}
                        </div>
                    }
                </Table.Cell>
            </Table.Row>
        );
    });

    return (
        <div>
            <Dimmer active={loading} inverted><Loader inverted>通信中..</Loader></Dimmer>
            <Message negative content={error} hidden={error === ''} />
            <Modal onClose={() => setConfirming(false)} open={confirming} closeOnDimmerClick={false}>
                <Modal.Content>
                    <p>{secondOperation ? '１眼目と２眼目の手術日程を確定します。よろしいですか？' : '手術日程を確定します。よろしいですか？'}</p>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setConfirming(false)}>いいえ</Button>
                    <Button positive onClick={commitAllAppointments}>はい</Button>
                </Modal.Actions>
            </Modal>

            {!operation &&
                <Page title="手術日程変更" onReset={onClose}>
                    <Message warning>手術日程を取得できませんでした。時間を置いて再度お試しください</Message>
                </Page>
            }

            {page === 'operation_date' && operation &&
                <Page title={`${secondOperation ? "１眼目：" : ""}${operationDayVisit.name}`} onRewind={onClose}>
                    <div style={{ maxWidth: '32em', margin: '0 auto' }}>
                        {(operation?.scheduling_policy?.status_message || secondOperation) &&
                            <Message info>
                                <Message.List>
                                    {operation?.scheduling_policy?.status_message &&
                                        <Message.Item>{operation.scheduling_policy.status_message}</Message.Item>
                                    }
                                </Message.List>
                            </Message>
                        }

                        <div style={{ margin: '1em 0' }}>
                            <h4>執刀医</h4>
                            {(!canSelectDoctor || ((operation.operation_doctors || []).length <= 1))
                                ? <div>{(operation.operation_doctors || [])[0]?.name || ''}</div>
                                : <Dropdown
                                    selection
                                    fluid
                                    value={operationDoctorCode}
                                    options={(operation.operation_doctors || []).map((doctor) => ({
                                        key: doctor.code,
                                        value: doctor.code,
                                        text: doctor.name
                                    }))}
                                    onChange={(e, { value }) => {
                                        setSelectedOperationDate(null);
                                        setOperationDoctorCode(value);
                                    }}
                                />
                            }
                        </div>

                        <h4>手術日（処置日）</h4>
                        <Calendar
                            month={operationMonth}
                            dates={operationCandidateDays}
                            onSelectDate={(date) => {
                                setSelectedOperationDate(date);
                                const dateStr = format(date, 'yyyy-MM-dd');
                                setLoading(true);
                                const allResources = Object.entries(resourceCodesByStepCode)
                                    .flatMap(([stepCode, codes]) => codes.map((r) => `${stepCode}:${r}`));
                                const firstFetch = apiSend('GET', `/operations/${operation.id}/candidate_steps`, {
                                    target_day: dateStr,
                                    doctor: operationDoctorCode,
                                    resources: allResources.length > 0 ? allResources.join(',') : null
                                });

                                const fetches = [firstFetch];

                                fetches[0]
                                    .then((stepAppointments) => {
                                        setLoading(false);
                                        const operationStepApp = stepAppointments.find((s) => s.step === '*');
                                        if (!operationStepApp?.appointment) {
                                            setError('この日は空きがありません。別の日付をお選びください');
                                            return;
                                        }
                                        setCandidateStepAppointments(stepAppointments);
                                        setSelectedOperationAppointment(operationStepApp.appointment);
                                        setSelectedAppointmentsByVisitId({ [operationDayVisit.id]: operationStepApp.appointment });
                                        setError('');
                                        setPage('schedules');
                                    })
                                    .catch((e) => {
                                        setError(e.message);
                                        setLoading(false);
                                    });
                            }}
                            onChangeMonth={(month) => {
                                setSelectedOperationDate(null);
                                setOperationMonth(month);
                            }}
                        />

                        {operationDays.length === 0 &&
                            <Message warning style={{ marginTop: '1em' }}>手術日（処置日）の空きがありません</Message>
                        }
                    </div>
                </Page>
            }

            {page === 'schedules' && operation &&
                <Page title={secondOperation ? `１眼目：${operation.name} 日程` : `${operation.name} 日程`} onRewind={() => setPage('operation_date')}>
                    <div style={{ maxWidth: '42em', margin: '0 auto' }}>
                        {operation?.scheduling_policy?.status_message &&
                            <Message info content={operation.scheduling_policy.status_message} />
                        }
                        <Table celled>
                            <Table.Body>
                                {renderStepRows(requiredStepCodes, stepByCode, visitByStepCode, resourceCodesByStepCode, (stepCode, newCodes) => {
                                    setResourceCodesByStepCode(prev => ({ ...prev, [stepCode]: newCodes }));
                                    setCandidateStepAppointments([]);
                                    setSelectedAppointmentsByVisitId({});
                                })}
                            </Table.Body>
                        </Table>

                        <div style={{ textAlign: 'center', marginTop: '1.5em' }}>
                            {secondOperation
                                ? <Button positive disabled={!isAllFirstEyeStepsSelected || !!error} onClick={() => {
                                    setSelectedSecondOperationDate(null);
                                    setSelectedSecondOperationAppointment(null);
                                    setSecondCandidateStepAppointments([]);
                                    if (selectedOperationDate) setSecondOperationMonth(startOfMonth(selectedOperationDate));
                                    setPage('second_operation_date');
                                }}>次へ（２眼目）</Button>
                                : <Button positive disabled={!isAllFirstEyeStepsSelected || !!error} onClick={() => setConfirming(true)}>確定</Button>
                            }
                        </div>
                    </div>
                </Page>
            }

            {page === 'second_operation_date' && secondOperation && secondOperationDayVisit &&
                <Page title={`２眼目：${secondOperationDayVisit.name}`} onRewind={() => initialPage === 'second_operation_date' ? onClose() : setPage('schedules')}>
                    <div style={{ maxWidth: '32em', margin: '0 auto' }}>
                        {secondOperation?.scheduling_policy?.status_message &&
                            <Message info content={secondOperation.scheduling_policy.status_message} />
                        }

                        <div style={{ margin: '1em 0' }}>
                            <h4>執刀医</h4>
                            {(!secondCanSelectDoctor || ((secondOperation.operation_doctors || []).length <= 1))
                                ? <div>{(secondOperation.operation_doctors || [])[0]?.name || ''}</div>
                                : <Dropdown
                                    selection
                                    fluid
                                    value={secondOperationDoctorCode}
                                    options={(secondOperation.operation_doctors || []).map((doctor) => ({
                                        key: doctor.code,
                                        value: doctor.code,
                                        text: doctor.name
                                    }))}
                                    onChange={(e, { value }) => {
                                        setSelectedSecondOperationDate(null);
                                        setSecondOperationDoctorCode(value);
                                    }}
                                />
                            }
                        </div>

                        <h4>手術日（処置日）</h4>
                        <Calendar
                            month={secondOperationMonth}
                            dates={secondOperationCandidateDays}
                            onSelectDate={(date) => {
                                setSelectedSecondOperationDate(date);
                                const dateStr = format(date, 'yyyy-MM-dd');
                                setLoading(true);
                                const allResources = Object.entries(secondResourceCodesByStepCode)
                                    .flatMap(([stepCode, codes]) => codes.map((r) => `${stepCode}:${r}`));
                                apiSend('GET', `/operations/${secondOperation.id}/candidate_steps`, {
                                    target_day: dateStr,
                                    resources: allResources.length > 0 ? allResources.join(',') : null
                                })
                                    .then((stepAppointments) => {
                                        setLoading(false);
                                        const secondOpStepApp = stepAppointments.find((s) => s.step === '*');
                                        if (!secondOpStepApp?.appointment) {
                                            setError('この日の空きがありません。別の日付をお選びください');
                                            return;
                                        }
                                        setSecondCandidateStepAppointments(stepAppointments);
                                        setSelectedSecondOperationAppointment(secondOpStepApp.appointment);
                                        setSelectedAppointmentsByVisitId((prev) => ({
                                            ...prev,
                                            [secondOperationDayVisit.id]: secondOpStepApp.appointment
                                        }));
                                        setError('');
                                        setPage('second_schedules');
                                    })
                                    .catch((e) => {
                                        setError(e.message);
                                        setLoading(false);
                                    });
                            }}
                            onChangeMonth={(month) => {
                                setSelectedSecondOperationDate(null);
                                setSecondOperationMonth(month);
                            }}
                        />

                        {secondOperationDays.length === 0 &&
                            <Message warning style={{ marginTop: '1em' }}>手術日（処置日）の空きがありません</Message>
                        }
                    </div>
                </Page>
            }

            {page === 'second_schedules' && secondOperation && secondOperationDayVisit &&
                <Page title="２眼目：手術日程" onRewind={() => setPage('second_operation_date')}>
                    <div style={{ maxWidth: '42em', margin: '0 auto' }}>
                        {secondOperation?.scheduling_policy?.status_message &&
                            <Message info content={secondOperation.scheduling_policy.status_message} />
                        }
                        <h4 style={{ marginBottom: '0.3em' }}>１眼目：{operation.name} 日程</h4>
                        <Table celled compact style={{ opacity: 0.75, marginBottom: '1.5em' }}>
                            <Table.Body>
                                {requiredStepCodes.map((stepCode) => {
                                    const step = stepByCode[stepCode];
                                    const visit = visitByStepCode[stepCode];
                                    if (!visit) return null;
                                    const selected = selectedAppointmentsByVisitId[visit.id];
                                    const optionResources = (stepCode !== '*' && step) ? (step.option_resources || []) : [];
                                    return (
                                        <Table.Row key={visit.id}>
                                            <Table.Cell>{step?.name || (visit.operations || []).find(op => op.id === operation?.id)?.step?.name}</Table.Cell>
                                            <Table.Cell>
                                                {selected ? (
                                                    <>
                                                        {getScheduleDateTime(selected.at)}
                                                        {selected.doctor && <span style={{ marginLeft: '0.5em', color: '#444' }}>{selected.doctor.name}</span>}
                                                    </>
                                                ) : '未選択'}
                                                {optionResources.length > 0 && (
                                                    <div style={{ marginTop: '0.4em', display: 'flex', flexDirection: 'column', gap: '0.2em' }}>
                                                        {optionResources.map((resource) => {
                                                            const isOn = (resourceCodesByStepCode[stepCode] || []).includes(resource.code);
                                                            return (
                                                                <div key={resource.code} style={{ fontSize: '0.85em', color: '#444' }}>
                                                                    {resource.name}：<span style={{ fontWeight: 'bold', color: isOn ? '#21ba45' : '#999' }}>{isOn ? 'あり' : 'なし'}</span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </Table.Cell>
                                        </Table.Row>
                                    );
                                })}
                            </Table.Body>
                        </Table>
                        <h4 style={{ marginBottom: '0.3em' }}>２眼目：{secondOperation.name} 日程</h4>
                        <Table celled>
                            <Table.Body>
                                {renderStepRows(secondRequiredStepCodes, secondStepByCode, secondVisitByStepCode, secondResourceCodesByStepCode, (stepCode, newCodes) => {
                                    setSecondResourceCodesByStepCode(prev => ({ ...prev, [stepCode]: newCodes }));
                                    setSecondCandidateStepAppointments([]);
                                    setSelectedAppointmentsByVisitId(prev => {
                                        const next = { ...prev };
                                        secondRequiredStepCodes.forEach(sc => {
                                            if (sc === '*') return; // 手術当日は残す
                                            const visit = secondVisitByStepCode[sc];
                                            if (visit?.id) delete next[visit.id];
                                        });
                                        return next;
                                    });
                                })}
                            </Table.Body>
                        </Table>

                        <div style={{ textAlign: 'center', marginTop: '1.5em' }}>
                            <Button positive disabled={!isAllSecondEyeStepsSelected || !!error} onClick={() => setConfirming(true)}>確定</Button>
                        </div>
                    </div>
                </Page>
            }

            {page === 'step_date' && stepPickVisit && operation &&
                <Page title="日時選択" onRewind={() => setPage(stepPickReturnPage)}>
                    <div style={{ maxWidth: '32em', margin: '0 auto' }}>
                        <h4>{stepPickVisit.name}</h4>
                        {stepPickCandidates.length === 0 ?
                            <Message warning>空きがありません</Message>
                            :
                            <>
                                <Calendar
                                    month={stepPickMonth}
                                    dates={[...new Set(stepPickCandidates.map((c) => c.at.date))].map((d) => parseISO(d))}
                                    onSelectDate={(date) => setStepPickSelectedDate(date)}
                                    onChangeMonth={(month) => {
                                        setStepPickSelectedDate(null);
                                        setStepPickMonth(month);
                                    }}
                                />
                                {stepPickSelectedDate &&
                                    <div style={{ marginTop: '1em' }}>
                                        {stepPickCandidates
                                            .filter((c) => c.at.date === format(stepPickSelectedDate, 'yyyy-MM-dd'))
                                            .sort((a, b) => a.at.time - b.at.time)
                                            .map((candidate) => (
                                                <Button
                                                    key={candidate.id}
                                                    fluid
                                                    positive
                                                    style={{ margin: '0.5em 0', display: 'flex', justifyContent: 'space-between' }}
                                                    onClick={() => {
                                                        setSelectedAppointmentsByVisitId((prev) => ({ ...prev, [stepPickVisit.id]: candidate }));
                                                        skipAutoSelectRef.current = true;
                                                        setPage(stepPickReturnPage);
                                                    }}
                                                >
                                                    <span>{getScheduleDateTime(candidate.at)}</span>
                                                    <span>{candidate.doctor?.name || ''}</span>
                                                </Button>
                                            ))
                                        }
                                    </div>
                                }
                            </>
                        }
                    </div>
                </Page>
            }
        </div>
    );
}

export default OperationBulkRescheduleForm;
